import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { useWarehouseStore } from '@/stores/warehouseStore';
import {
  getAllWorkOrders,
  updateWorkOrder,
  startWorkOrderApi,
  createWorkOrder,
  completeWorkOrderApi,
  assignWorkOrderApi,
} from '@/api/workorder.api';
import WorkOrderDetailDrawer, { WO_DISPLAY_STATUS } from './components/WorkOrderDetailDrawer';
import { getAllProductionOrders } from '@/api/productionOrder.api'
import { getAllWorkCenters } from '@/api/workcenter.api';
import { getAllMachines } from '@/api/machine.api';
import { getAllOperators } from '@/api/operators.api';
import { getAllShifts } from '@/api/shift.api';
import WorkOrderEditModal from './components/WorkOrderEditModal';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';
import { useWorkCenters } from '@/hooks/useWorkCenters';

type WOStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'ON_HOLD' | 'QC_PENDING' | 'QC_FAILED' | 'COMPLETED_QC_PENDING'
  // Shown for a PENDING work order: Locked (previous stage open), Planned (no crew yet), Assigned
  | 'LOCKED' | 'PLANNED' | 'ASSIGNED'
  // Shown for an IN_PROGRESS work order with an open pause (downtime)
  | 'PAUSED';

const woStatusConfig: Record<
  string,
  { label: string; bg: string; text: string; border: string; icon: string }
> = {
  PENDING: {
    label: 'Pending',
    bg: 'bg-slate-100',
    text: 'text-slate-600',
    border: 'border-slate-200',
    icon: 'ri-time-line',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    icon: 'ri-loader-4-line',
  },
  COMPLETED: {
    label: 'Completed',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    icon: 'ri-check-line',
  },
  QC_PENDING: {
    label: 'QC Pending',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-200',
    icon: 'ri-microscope-line',
  },
  COMPLETED_QC_PENDING: {
    label: 'QC Pending',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    border: 'border-purple-200',
    icon: 'ri-microscope-line',
  },
  QC_FAILED: {
    label: 'QC Failed',
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    border: 'border-rose-200',
    icon: 'ri-close-circle-line',
  },
  ON_HOLD: {
    label: 'On Hold',
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    border: 'border-rose-200',
    icon: 'ri-pause-circle-line',
  },
};

function formatShortDate(dateStr: string): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
}

const mapResponseToWorkOrder = (w: any) => ({
  id: w.id,
  woNumber: w.wo_number ?? w.woNumber,
  productionOrderId: w.production_order_id ?? w.productionOrderId,
  productionOrderNumber: w.production_order_number ?? w.productionOrderNumber,
  stageNumber: Number(w.stage_number ?? w.stageNumber ?? 0),
  operatorsRequired: Number(w.operators_required ?? w.operatorsRequired ?? 1),
  stageType: w.stage_type ?? w.stageType ?? 'IN_HOUSE',
  stageName: w.stage_name ?? w.stageName ?? '',
  workCenterId: w.work_center_id ?? w.workCenterId ?? '',
  workCenterName: w.work_center_name ?? w.workCenterName ?? '',
  machineId: w.machine_id ?? w.machineId ?? null,
  machineName: w.machine_name ?? w.machineName ?? null,
  operatorId: w.operator_id ?? w.operatorId ?? null,
  operatorIds: Array.isArray(w.operator_ids) ? w.operator_ids.map(String) : (w.operator_id ? [String(w.operator_id)] : []),
  operatorName: w.operator_name ?? w.operatorName ?? null,
  shiftId: w.shift_id ?? w.shiftId ?? null,
  shiftName: w.shift_name ?? w.shiftName ?? null,
  plannedQty: Number(w.planned_qty ?? w.plannedQty ?? 0),
  completedQty: Number(w.completed_qty ?? w.completedQty ?? 0),
  rejectedQty: Number(w.rejected_qty ?? w.rejectedQty ?? 0),
  status: w.status ?? 'PENDING',
  isLocked: Boolean(w.is_locked ?? w.isLocked),
  qcRequired: Boolean(w.qc_required ?? w.qcRequired),
  checklistName: w.checklist_name ?? w.checklistName ?? '',
  checklistId: w.checklist_id ?? w.checklistId ?? null,
  qcInspectionId: w.qc_inspection_id ?? w.qcInspectionId ?? null,
  qcResult: w.qc_result ?? w.qcResult ?? null,
  plannedStartDate: w.planned_start_date ? new Date(w.planned_start_date).toISOString().split('T')[0] : '',
  plannedEndDate: w.planned_end_date ? new Date(w.planned_end_date).toISOString().split('T')[0] : '',
  actualStartDate: w.actual_start_date ? new Date(w.actual_start_date).toISOString().split('T')[0] : null,
  actualEndDate: w.actual_end_date ? new Date(w.actual_end_date).toISOString().split('T')[0] : null,
  plannedTimeMinutes: Number(w.planned_time_minutes ?? w.plannedTimeMinutes ?? 0),
  assignedAt: w.assigned_at ?? null,
  isPaused: Boolean(w.is_paused),
  plannedStartAt: w.planned_start_at ?? null,
  plannedEndAt: w.planned_end_at ?? null,
  actualTimeMinutes: w.actual_time_minutes ? Number(w.actual_time_minutes) : null,
  notes: w.notes ?? '',
  warehouseId: w.warehouse_id ?? w.warehouseId,
  warehouseName: w.warehouse_name ?? w.warehouseName ?? null,
  itemName: w.item_name ?? w.itemName ?? null,
  itemCode: w.item_code ?? w.itemCode ?? null,
  isActive: w.is_active ?? w.isActive ?? true,
  createdAt: w.created_at ?? w.createdAt ?? '',
  updatedAt: w.updated_at ?? w.updatedAt ?? '',
});

export default function WorkOrdersPage() {
  // Work center shown as "WC-001 · Assembly Line"
  const workCenterLookup = useWorkCenters();
  const navigate = useNavigate();
  const toast = useToast();
  const { selectedWarehouseId, selectedWarehouseName } = useWarehouseStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const poIdFromUrl = searchParams.get('poId');

  // ── API State ──
  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [productionOrders, setProductionOrders] = useState<any[]>([]);
  const [workCenters, setWorkCenters] = useState<any[]>([]);
  const [machines, setMachines] = useState<any[]>([]);
  const [operators, setOperators] = useState<any[]>([]);
  const [shifts, setShifts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // ── Filter state ──
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<WOStatus | 'ALL'>('ALL');
  const [workCenterFilter, setWorkCenterFilter] = useState<string>('ALL');
  const [machineFilter, setMachineFilter] = useState<string>('ALL');
  const [poFilter, setPoFilter] = useState<string>(poIdFromUrl || 'ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // ── Edit modal state ──
  const [editWO, setEditWO] = useState<any | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editMode, setEditMode] = useState<'edit' | 'assign' | 'start' | 'rework'>('edit');
  // Work order shown in the detail panel
  const [detailId, setDetailId] = useState<string | null>(null);

  const [actionLoading, setActionLoading] = useState(false);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [
        woRes,
        poRes,
        wcRes,
        mcRes,
        opRes,
        shiftRes,
      ] = await Promise.all([
        getAllWorkOrders(selectedWarehouseId || undefined),
        getAllProductionOrders({ warehouseId: selectedWarehouseId || undefined }),
        getAllWorkCenters(selectedWarehouseId || undefined),
        getAllMachines(selectedWarehouseId || undefined),
        getAllOperators(),
        getAllShifts(),
      ]);

      if (woRes.success && woRes.data) {
        setWorkOrders(woRes.data.map(mapResponseToWorkOrder));
      }
      if (poRes.success && poRes.data) {
        setProductionOrders(poRes.data);
      }
      if (wcRes.success && wcRes.data) {
        setWorkCenters(wcRes.data);
      }
      if (mcRes.success && mcRes.data) {
        setMachines(mcRes.data);
      }
      if (opRes.success && opRes.data) {
        setOperators(opRes.data);
      }
      if (shiftRes.success && shiftRes.data) {
        setShifts(shiftRes.data);
      }
    } catch (err: any) {
      console.error('Failed to load Work Orders data:', err);
      toast.error(err?.message || 'Failed to load work orders data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedWarehouseId]);

  // Synchronize PO Filter if URL param changes
  useEffect(() => {
    if (poIdFromUrl) {
      setPoFilter(poIdFromUrl);
    }
  }, [poIdFromUrl]);

  // ── Warehouse Partitioned Masters ──
  const warehouseWorkCenters = useMemo(() => {
    if (!selectedWarehouseId) return workCenters;
    return workCenters.filter(
      (wc) => (!wc.warehouse_id && !wc.warehouseId) || wc.warehouse_id === selectedWarehouseId || wc.warehouseId === selectedWarehouseId
    );
  }, [workCenters, selectedWarehouseId]);

  const warehouseMachines = useMemo(() => {
    if (!selectedWarehouseId) return machines;
    return machines.filter(
      (m) => (!m.warehouse_id && !m.warehouseId) || m.warehouse_id === selectedWarehouseId || m.warehouseId === selectedWarehouseId
    );
  }, [machines, selectedWarehouseId]);

  const warehouseOperators = useMemo(() => {
    if (!selectedWarehouseId) return operators;
    return operators.filter(
      (o) => (!o.warehouse_id && !o.warehouseId) || o.warehouse_id === selectedWarehouseId || o.warehouseId === selectedWarehouseId
    );
  }, [operators, selectedWarehouseId]);

  const warehouseShifts = useMemo(() => {
    if (!selectedWarehouseId) return shifts;
    return shifts.filter(
      (s) => (!s.warehouse_id && !s.warehouseId) || s.warehouse_id === selectedWarehouseId || s.warehouseId === selectedWarehouseId
    );
  }, [shifts, selectedWarehouseId]);

  const warehouseProductionOrders = useMemo(() => {
    if (!selectedWarehouseId) return productionOrders;
    return productionOrders.filter(
      (po) => (!po.warehouse_id && !po.warehouseId) || po.warehouse_id === selectedWarehouseId || po.warehouseId === selectedWarehouseId
    );
  }, [productionOrders, selectedWarehouseId]);

  // ── Unique POs for dropdown ──
  const uniquePOs = useMemo(() => {
    const ids = [...new Set(workOrders.map((w) => w.productionOrderId))];
    return ids.map((id) => {
      const po = warehouseProductionOrders.find((p) => p.id === id);
      return { id, number: po?.po_number || po?.poNumber || id };
    });
  }, [workOrders, warehouseProductionOrders]);

  const poFilterOptions: SearchableOption[] = useMemo(() => [
    { value: 'ALL', label: 'All Orders' },
    ...uniquePOs.map((po) => ({ value: po.id, label: po.number })),
  ], [uniquePOs]);

  // A stage is locked until every earlier stage of its order is completed (and passed QC)
  const lockInfo = useCallback((wo: any) => {
    const uncompletedPrev = workOrders.find(
      (w) => w.productionOrderId === wo.productionOrderId && w.stageNumber < wo.stageNumber && w.status !== 'COMPLETED',
    );
    return { isLocked: Boolean(wo.isLocked || uncompletedPrev), uncompletedPrev };
  }, [workOrders]);
  // Lifecycle shown: Locked → Planned → Assigned → In Progress (Paused) → QC Pending → Completed
  const displayStatusOf = useCallback((wo: any): string => {
    if (wo.status === 'IN_PROGRESS' && wo.isPaused) return 'PAUSED';
    if (wo.status !== 'PENDING') return wo.status;
    if (lockInfo(wo).isLocked) return 'LOCKED';
    return wo.assignedAt ? 'ASSIGNED' : 'PLANNED';
  }, [lockInfo]);

  const statusFilterOptions: SearchableOption[] = useMemo(() => [
    { value: 'ALL', label: 'All Status' },
    { value: 'LOCKED', label: 'Locked' },
    { value: 'PLANNED', label: 'Planned' },
    { value: 'ASSIGNED', label: 'Assigned' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'PAUSED', label: 'Paused' },
    { value: 'QC_PENDING', label: 'QC Pending' },
    { value: 'QC_FAILED', label: 'QC Failed' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'ON_HOLD', label: 'On Hold' },
  ], []);

  const workCenterFilterOptions: SearchableOption[] = useMemo(() => [
    { value: 'ALL', label: 'All Centers' },
    ...warehouseWorkCenters.map((wc) => ({ value: wc.id, label: workCenterLookup.labelOf(wc.id, wc.name) })),
  ], [warehouseWorkCenters]);

  const machineFilterOptions: SearchableOption[] = useMemo(() => [
    { value: 'ALL', label: 'All Machines' },
    ...warehouseMachines.map((m) => ({
      value: String(m.id),
      label: m.name,
      sublabel: m.machineCode || m.code || undefined,
    })),
  ], [warehouseMachines]);

  // ── Filtered work orders ──
  const filtered = useMemo(() => {
    let list = [...workOrders];
    if (selectedWarehouseId) {
      list = list.filter((w) => !w.warehouseId || w.warehouseId === selectedWarehouseId);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (w) =>
          w.woNumber.toLowerCase().includes(q) ||
          w.stageName.toLowerCase().includes(q) ||
          w.productionOrderNumber.toLowerCase().includes(q) ||
          (w.workCenterName && w.workCenterName.toLowerCase().includes(q)) ||
          (w.machineName && w.machineName.toLowerCase().includes(q))
      );
    }
    if (statusFilter !== 'ALL') {
      list = list.filter((w) => {
        const shown = displayStatusOf(w);
        return statusFilter === 'QC_PENDING' ? ['QC_PENDING', 'COMPLETED_QC_PENDING'].includes(shown) : shown === statusFilter;
      });
    }
    if (workCenterFilter !== 'ALL') {
      list = list.filter((w) => String(w.workCenterId) === String(workCenterFilter));
    }
    if (machineFilter !== 'ALL') {
      list = list.filter((w) => String(w.machineId) === String(machineFilter));
    }
    if (poFilter !== 'ALL') {
      list = list.filter((w) => w.productionOrderId === poFilter);
    }
    if (dateFrom) {
      list = list.filter((w) => w.plannedStartDate >= dateFrom);
    }
    if (dateTo) {
      list = list.filter((w) => w.plannedStartDate <= dateTo);
    }
    return list.sort((a, b) => a.stageNumber - b.stageNumber);
  }, [workOrders, search, statusFilter, workCenterFilter, machineFilter, poFilter, dateFrom, dateTo, selectedWarehouseId, displayStatusOf]);

  // ── Summary counts ──
  const counts = useMemo(() => {
    const total = filtered.length;
    const pending = filtered.filter((w) => w.status === 'PENDING').length;
    const inProgress = filtered.filter(
      (w) => w.status === 'IN_PROGRESS',
    ).length;
    const completed = filtered.filter((w) => w.status === 'COMPLETED').length;
    return { total, pending, inProgress, completed };
  }, [filtered]);

  // ── Actions ──
  const handleStart = useCallback(
    async (wo: any) => {
      // Sequential Stage Guard
      const prevStages = workOrders.filter(
        (w) => w.productionOrderId === wo.productionOrderId && w.stageNumber < wo.stageNumber
      );
      const uncompletedPrev = prevStages.find((w) => w.status !== 'COMPLETED');
      if (uncompletedPrev) {
        toast.error(`Cannot start Stage ${wo.stageNumber}. Previous stage (Stage ${uncompletedPrev.stageNumber}) is ${uncompletedPrev.status}. Stages must be completed in sequential order.`);
        return;
      }

      setEditWO(wo);
      // A planned stage is assigned first; an assigned one is started
      setEditMode(wo.assignedAt ? 'start' : 'assign');
      setEditOpen(true);
    },
    [workOrders, toast],
  );

  const handleAssign = useCallback((wo: any) => {
    setEditWO(wo);
    setEditMode('assign');
    setEditOpen(true);
  }, []);

  const handleConfirmAssign = useCallback(
    async (updated: any) => {
      if (actionLoading) return;
      setActionLoading(true);
      try {
        const res = await assignWorkOrderApi(updated.id, {
          operator_ids: updated.operatorIds || [],
          machine_id: updated.machineId || null,
          shift_id: updated.shiftId || null,
          work_center_id: updated.workCenterId || null,
          planned_start_at: updated.plannedStartAt,
          planned_end_at: updated.plannedEndAt || undefined,
        });
        if (res.success) {
          toast.success(res.message || `Work Order ${updated.woNumber} assigned`);
          setEditOpen(false);
          setEditWO(null);
          loadData();
        } else {
          toast.error(res.message || 'Failed to assign Work Order');
        }
      } catch (err: any) {
        toast.error(err.response?.data?.message || err.message || 'Server error occurred');
      } finally {
        setActionLoading(false);
      }
    },
    [toast, actionLoading],
  );

  // Open the start card for a Work Order handed over via URL (e.g. from Production Order detail)
  const startWoIdFromUrl = searchParams.get('startWo');
  useEffect(() => {
    if (!startWoIdFromUrl || isLoading || workOrders.length === 0) return;
    const wo = workOrders.find((w) => String(w.id) === String(startWoIdFromUrl));
    const next = new URLSearchParams(searchParams);
    next.delete('startWo');
    setSearchParams(next, { replace: true });
    if (!wo) {
      toast.error('Work Order not found');
    } else if (wo.status !== 'PENDING') {
      toast.error(`Work Order ${wo.woNumber} is already ${wo.status}`);
    } else {
      handleStart(wo);
    }
  }, [startWoIdFromUrl, isLoading, workOrders]);

  // Open the rework card for a QC-failed Work Order handed over via URL
  const reworkWoIdFromUrl = searchParams.get('reworkWo');
  useEffect(() => {
    if (!reworkWoIdFromUrl || isLoading || workOrders.length === 0) return;
    const wo = workOrders.find((w) => String(w.id) === String(reworkWoIdFromUrl));
    const next = new URLSearchParams(searchParams);
    next.delete('reworkWo');
    setSearchParams(next, { replace: true });
    if (!wo) {
      toast.error('Work Order not found');
    } else if (wo.status !== 'QC_FAILED') {
      toast.error(`Work Order ${wo.woNumber} is ${wo.status}, rework applies only to QC failed stages`);
    } else {
      setEditWO(wo);
      setEditMode('rework');
      setEditOpen(true);
    }
  }, [reworkWoIdFromUrl, isLoading, workOrders]);

  // `source` is the QC-failed Work Order merged with the details entered on the card
  const handleConfirmRework = useCallback(
    async (source: any) => {
      if (actionLoading) return;
      setActionLoading(true);
      try {
        const baseStage = String(source.stageName || '').replace(/\s*—\s*Rework$/i, '');
        const res = await createWorkOrder({
          productionOrderId: source.productionOrderId,
          productionOrderNumber: source.productionOrderNumber,
          stageNumber: source.stageNumber,
          stageName: `${baseStage} — Rework`,
          type: 'REWORK',
          workCenterId: source.workCenterId,
          workCenterName: source.workCenterName,
          machineId: source.machineId || null,
          machineName: source.machineName || null,
          operatorId: source.operatorId || null,
          operatorName: source.operatorName || null,
          shiftId: source.shiftId || null,
          shiftName: source.shiftName || null,
          plannedQty: source.plannedQty,
          plannedStartDate: source.plannedStartDate,
          plannedEndDate: source.plannedEndDate,
          plannedTimeMinutes: source.plannedTimeMinutes,
          notes: source.notes,
          warehouseId: source.warehouseId || null,
        });
        if (res.success) {
          toast.success(`Rework Work Order ${res.data?.wo_number || ''} created for ${source.woNumber}`);
          setEditOpen(false);
          setEditWO(null);
          loadData();
        } else {
          toast.error(res.message || 'Failed to create rework Work Order');
        }
      } catch (err: any) {
        toast.error(err.response?.data?.message || err.message || 'Server error occurred');
      } finally {
        setActionLoading(false);
      }
    },
    [toast, actionLoading],
  );

  const handleConfirmStart = useCallback(
    async (updated: any) => {
      if (actionLoading) return;
      setActionLoading(true);
      try {
        const today = new Date().toLocaleDateString('en-CA');
        const res = await startWorkOrderApi(updated.id, {
          work_center_id: updated.workCenterId || undefined,
          work_center_name: updated.workCenterName || undefined,
          operator_id: updated.operatorId || undefined,
          operator_ids: updated.operatorIds,
          operator_name: updated.operatorName,
          machine_id: updated.machineId || undefined,
          machine_name: updated.machineName,
          shift_id: updated.shiftId || undefined,
          shift_name: updated.shiftName,
          start_time:
            updated.actualStartDate && updated.actualStartDate !== today
              ? new Date(updated.actualStartDate).toISOString()
              : new Date().toISOString(),
          notes: updated.notes || undefined,
        });
        if (res.success) {
          toast.success(`Work Order ${updated.woNumber} started`);
          setEditOpen(false);
          setEditWO(null);
          loadData();
        } else {
          toast.error(res.message || 'Failed to start Work Order');
        }
      } catch (err: any) {
        toast.error(err.response?.data?.message || err.message || 'Server error occurred');
      } finally {
        setActionLoading(false);
      }
    },
    [toast, actionLoading],
  );

  const handleComplete = useCallback(
    async (wo: any) => {
      try {
        // Completed / rejected quantities are taken from the stage's production entries on the server
        const res = await completeWorkOrderApi(wo.id, {});

        // Case 1: QC required and blocked -> DO NOT OPEN ANY POPUP CARD! REDIRECT DIRECTLY!
        if ((res as any).blockedByQc || (res as any).status === 'QC_PENDING' || res.data?.status === 'QC_PENDING') {
          const targetInspectionId = (res as any).inspection_id || wo.qcInspectionId || '';
          const woNum = (res as any).wo_number || wo.woNumber;
          const currentUrl = window.location.pathname + window.location.search;
          navigate(`/manufacturing/qc/inprocess?wo=${encodeURIComponent(woNum)}&inspection_id=${targetInspectionId}&return_url=${encodeURIComponent(currentUrl)}`);
          return;
        }

        // Case 2: QC enabled = NO (or passed)
        if (res.success) {
          toast.success(`✅ Work Order ${wo.woNumber} completed! Next stage unlocked.`);
          loadData();
        } else {
          toast.error(res.message || 'Failed to complete Work Order');
        }
      } catch (err: any) {
        toast.error(err.response?.data?.message || err.message || 'Server error occurred');
        // No production recorded yet: take the user to the Shop Floor entry form for this Work Order
        if (err.response?.data?.code === 'PRODUCTION_ENTRY_REQUIRED') {
          const currentUrl = window.location.pathname + window.location.search;
          navigate(`/manufacturing/shop-floor?woId=${wo.id}&return_url=${encodeURIComponent(currentUrl)}`);
        }
      }
    },
    [toast, loadData, navigate],
  );

  const handleEdit = useCallback((wo: any) => {
    setEditWO(wo);
    setEditMode('edit');
    setEditOpen(true);
  }, []);

  const handleSaveEdit = useCallback(
    async (updated: any) => {
      try {
        const res = await updateWorkOrder(updated.id, {
          workCenterId: updated.workCenterId,
          workCenterName: updated.workCenterName,
          machineId: updated.machineId,
          machineName: updated.machineName,
          operatorId: updated.operatorId,
          operatorIds: updated.operatorIds,
          operatorName: updated.operatorName,
          shiftId: updated.shiftId,
          shiftName: updated.shiftName,
          actualStartDate: updated.actualStartDate,
          actualEndDate: updated.actualEndDate,
          notes: updated.notes,
        });
        if (res.success) {
          toast.success(`Work Order ${updated.woNumber} updated`);
          setEditOpen(false);
          setEditWO(null);
          loadData();
        } else {
          toast.error(res.message || 'Failed to update Work Order');
        }
      } catch (err: any) {
        toast.error(err.message || 'Server error occurred');
      }
    },
    [toast],
  );

  const handleClearFilters = useCallback(() => {
    setSearch('');
    setStatusFilter('ALL');
    setWorkCenterFilter('ALL');
    setMachineFilter('ALL');
    setPoFilter('ALL');
    setDateFrom('');
    setDateTo('');
    if (poIdFromUrl) {
      setSearchParams({});
    }
  }, [poIdFromUrl, setSearchParams]);

  return (
    <AppLayout>
      <div className="min-h-screen bg-[#f8fafc] py-6 px-4 md:px-6">
        <div className="max-w-[1400px] mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-[#1e293b]">Work Orders</h1>
                {selectedWarehouseName && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                    <i className="ri-store-2-line text-xs" />
                    {selectedWarehouseName}
                  </span>
                )}
              </div>
              <p className="text-xs text-[#64748b] mt-0.5">
                {counts.total} work orders
                {counts.pending > 0 && (
                  <span className="ml-2 text-slate-500">
                    | {counts.pending} pending
                  </span>
                )}
                {counts.inProgress > 0 && (
                  <span className="ml-2 text-amber-600">
                    | {counts.inProgress} in progress
                  </span>
                )}
                {counts.completed > 0 && (
                  <span className="ml-2 text-emerald-600">
                    | {counts.completed} completed
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={() => navigate('/manufacturing/production-orders')}
              className="h-9 px-4 rounded-lg border border-slate-200 text-sm font-medium text-[#64748b] hover:bg-white cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-arrow-left-line" />
              Back to Orders
            </button>
          </div>

          {/* Filters */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
            <div className="flex flex-wrap items-end gap-3">
              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <label className="block text-[11px] font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                  Search
                </label>
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="WO number, PO or stage..."
                    className="w-full h-10 pl-9 pr-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-indigo-100"
                  />
                </div>
              </div>

              {/* PO Filter */}
              <div className="w-[200px]">
                <label className="block text-[11px] font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                  Production Order
                </label>
                <SearchableSelect
                  value={poFilter}
                  onChange={setPoFilter}
                  options={poFilterOptions}
                  placeholder="All Orders"
                  searchPlaceholder="Search order..."
                />
              </div>

              {/* Status */}
              <div className="w-[160px]">
                <label className="block text-[11px] font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                  Status
                </label>
                <SearchableSelect
                  value={statusFilter}
                  onChange={(v) => setStatusFilter(v as WOStatus | 'ALL')}
                  options={statusFilterOptions}
                  placeholder="All Status"
                  searchPlaceholder="Search status..."
                />
              </div>

              {/* Work Center */}
              <div className="w-[180px]">
                <label className="block text-[11px] font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                  Work Center
                </label>
                <SearchableSelect
                  value={workCenterFilter}
                  onChange={setWorkCenterFilter}
                  options={workCenterFilterOptions}
                  placeholder="All Centers"
                  searchPlaceholder="Search center..."
                />
              </div>

              {/* Machine */}
              <div className="w-[180px]">
                <label className="block text-[11px] font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                  Machine
                </label>
                <SearchableSelect
                  value={machineFilter}
                  onChange={setMachineFilter}
                  options={machineFilterOptions}
                  placeholder="All Machines"
                  searchPlaceholder="Search machine..."
                />
              </div>

              {/* Date range */}
              <div className="flex gap-2">
                <div>
                  <label className="block text-[11px] font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                    From
                  </label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-[140px] h-10 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-indigo-100"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-[#64748b] uppercase tracking-wide mb-1">
                    To
                  </label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-[140px] h-10 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-indigo-100"
                  />
                </div>
              </div>

              {/* Clear */}
              <button
                onClick={handleClearFilters}
                className="h-10 px-3 rounded-lg border border-slate-200 text-sm text-[#64748b] hover:bg-white cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                <i className="ri-filter-off-line" />
                Clear
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] whitespace-nowrap">
                      WO Number
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] whitespace-nowrap">
                      Production Order
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] whitespace-nowrap">
                      Stage
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] whitespace-nowrap">
                      Work Center
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] whitespace-nowrap">
                      Machine
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] whitespace-nowrap">
                      Operator
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] whitespace-nowrap">
                      Planned Dates
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] whitespace-nowrap">
                      Progress
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] whitespace-nowrap">
                      Status
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-[#64748b] whitespace-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan={10}
                        className="px-4 py-12 text-center text-sm text-[#94a3b8]"
                      >
                        Loading work orders...
                      </td>
                    </tr>
                  ) : filtered.map((wo) => {
                    const shownStatus = displayStatusOf(wo);
                    const shownCfg = WO_DISPLAY_STATUS[shownStatus];
                    const progressPct =
                      wo.plannedQty > 0
                        ? Math.round((wo.completedQty / wo.plannedQty) * 100)
                        : 0;

                    // Sequential Stage Logic
                    const { isLocked, uncompletedPrev } = lockInfo(wo);
                    const isQcPending = wo.status === 'QC_PENDING' || wo.status === 'COMPLETED_QC_PENDING';
                    const isQcFailed = wo.status === 'QC_FAILED';

                    return (
                      <tr
                        key={wo.id}
                        onClick={() => setDetailId(wo.id)}
                        title="Open work order"
                        className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <span className="font-medium text-[#4f46e5]">
                            {wo.woNumber}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/manufacturing/production-orders/${wo.productionOrderId}`);
                            }}
                            className="text-sm text-[#475569] hover:text-[#4f46e5] cursor-pointer whitespace-nowrap block font-medium"
                          >
                            {wo.productionOrderNumber}
                          </button>
                          {wo.warehouseName && (
                            <span className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                              <i className="ri-store-2-line text-[10px] text-slate-400" />
                              {wo.warehouseName}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-slate-100 text-[10px] font-semibold text-slate-600">
                              {wo.stageNumber}
                            </span>
                            <span className="text-sm text-[#1e293b]">
                              {wo.stageName}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-[#475569]">
                          {workCenterLookup.labelOf((wo as any).workCenterId ?? (wo as any).work_center_id, wo.workCenterName)}
                          {Number((wo as any).operatorsRequired) > 1 && (
                            <span className="ml-1.5 text-[10px] font-semibold text-slate-500" title="Operators needed on this stage">
                              <i className="ri-group-line" /> {(wo as any).operatorsRequired}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {wo.machineName ? (
                            <span className="text-sm text-[#475569]">
                              {wo.machineName}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400 italic">
                              — Not assigned —
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {wo.operatorName ? (
                            <span className="text-sm text-[#475569]">
                              {wo.operatorName}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400 italic">
                              — Not assigned —
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-[#475569] whitespace-nowrap">
                          {formatShortDate(wo.plannedStartDate)}{' '}
                          <span className="text-slate-300 mx-1">→</span>{' '}
                          {formatShortDate(wo.plannedEndDate)}
                          {wo.plannedStartAt && (
                            <span className="block text-[11px] text-slate-400">
                              {new Date(wo.plannedStartAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                              {wo.plannedEndAt ? ` – ${new Date(wo.plannedEndAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : ''}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${progressPct === 100 ? 'bg-emerald-500' : progressPct > 0 ? 'bg-amber-500' : 'bg-slate-300'}`}
                                style={{ width: `${progressPct}%` }}
                              />
                            </div>
                            <span className="text-xs text-[#64748b] whitespace-nowrap">
                              {wo.completedQty}/{wo.plannedQty}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {shownCfg ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border ${shownCfg.cls}`}>
                              <i className={shownCfg.icon} />
                              {shownCfg.label}
                            </span>
                          ) : (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border ${(woStatusConfig[wo.status] || woStatusConfig.PENDING).bg} ${(woStatusConfig[wo.status] || woStatusConfig.PENDING).text} ${(woStatusConfig[wo.status] || woStatusConfig.PENDING).border}`}>
                              <i className={(woStatusConfig[wo.status] || woStatusConfig.PENDING).icon} />
                              {(woStatusConfig[wo.status] || woStatusConfig.PENDING).label}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1.5">
                            {wo.status === 'PENDING' && (
                              isLocked ? (
                                <span
                                  className="h-8 px-2.5 rounded-md bg-slate-100 text-slate-400 border border-slate-200 text-xs font-semibold inline-flex items-center gap-1 cursor-not-allowed select-none"
                                  title={
                                    uncompletedPrev
                                      ? `Stage ${uncompletedPrev.stageNumber} (${uncompletedPrev.stageName}) must be completed first.`
                                      : 'Locked: Complete previous stage first'
                                  }
                                >
                                  <i className="ri-lock-line text-xs" />
                                  Locked
                                </span>
                              ) : shownStatus === 'ASSIGNED' ? (
                                <>
                                  <button
                                    onClick={() => handleStart(wo)}
                                    className="h-8 px-2.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 text-xs font-medium cursor-pointer whitespace-nowrap flex items-center gap-1 shadow-xs"
                                    title="Start Work Order"
                                  >
                                    <i className="ri-play-line" />
                                    Start
                                  </button>
                                  <button
                                    onClick={() => handleAssign(wo)}
                                    className="h-8 w-8 flex items-center justify-center rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50 cursor-pointer"
                                    title="Reassign crew / machine / time"
                                  >
                                    <i className="ri-user-shared-line" />
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => handleAssign(wo)}
                                  className="h-8 px-2.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-medium hover:bg-emerald-100 cursor-pointer whitespace-nowrap flex items-center gap-1"
                                  title="Assign crew, machine and planned time"
                                >
                                  <i className="ri-user-shared-line" />
                                  Assign
                                </button>
                              )
                            )}
                            {wo.status === 'IN_PROGRESS' && (
                              <button
                                onClick={() => handleComplete(wo)}
                                className="h-8 px-2.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 text-xs font-medium cursor-pointer whitespace-nowrap flex items-center gap-1 shadow-xs"
                                title="Complete Work Order"
                              >
                                <i className="ri-check-double-line" />
                                Complete
                              </button>
                            )}
                            {isQcPending && (
                              <button
                                onClick={() => {
                                  const currentUrl = window.location.pathname + window.location.search;
                                  navigate(`/manufacturing/qc/inprocess?wo=${encodeURIComponent(wo.woNumber)}&inspection_id=${wo.qcInspectionId || ''}&return_url=${encodeURIComponent(currentUrl)}`);
                                }}
                                className="h-8 px-2.5 rounded-md bg-purple-600 text-white hover:bg-purple-700 text-xs font-semibold cursor-pointer whitespace-nowrap flex items-center gap-1 shadow-xs"
                                title="Complete Mandatory Quality Inspection"
                              >
                                <i className="ri-microscope-line" />
                                Inspect QC
                              </button>
                            )}
                            {isQcFailed && (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => {
                                    const currentUrl = window.location.pathname + window.location.search;
                                    navigate(`/manufacturing/qc/inprocess?wo=${encodeURIComponent(wo.woNumber)}&inspection_id=${wo.qcInspectionId || ''}&return_url=${encodeURIComponent(currentUrl)}`);
                                  }}
                                  className="h-8 px-2.5 rounded-md bg-purple-600 text-white hover:bg-purple-700 text-xs font-semibold cursor-pointer whitespace-nowrap flex items-center gap-1 shadow-xs"
                                  title="Inspect QC (Re-Inspection)"
                                >
                                  <i className="ri-microscope-line" />
                                  Inspect QC
                                </button>
                                <span
                                  className="h-8 px-2 rounded-md bg-rose-50 text-rose-700 border border-rose-200 text-xs font-medium inline-flex items-center gap-1"
                                >
                                  <i className="ri-close-circle-line" />
                                  QC Failed
                                </span>
                              </div>
                            )}
                            {wo.status === 'COMPLETED' && (
                              <span
                                className="h-8 px-2.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-medium inline-flex items-center gap-1"
                              >
                                <i className="ri-check-line text-emerald-600" />
                                Done
                              </span>
                            )}
                            <button
                              onClick={() => handleEdit(wo)}
                              className="h-8 w-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 cursor-pointer"
                              title="Edit Work Order"
                            >
                              <i className="ri-edit-line" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!isLoading && filtered.length === 0 && (
                    <tr>
                      <td
                        colSpan={10}
                        className="px-4 py-12 text-center text-sm text-[#94a3b8]"
                      >
                        <div className="flex flex-col items-center gap-2">
                          <i className="ri-search-line text-2xl text-slate-300" />
                          <p>No work orders match your filters</p>
                          <button
                            onClick={handleClearFilters}
                            className="text-[#4f46e5] text-xs font-medium hover:underline cursor-pointer"
                          >
                            Clear all filters
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <WorkOrderEditModal
        open={editOpen}
        workOrder={editWO}
        onClose={() => {
          setEditOpen(false);
          setEditWO(null);
        }}
        onSave={editMode === 'rework' ? handleConfirmRework : editMode === 'assign' ? handleConfirmAssign : editMode === 'start' ? handleConfirmStart : handleSaveEdit}
        mode={editMode}
        machines={warehouseMachines}
        operators={warehouseOperators}
        shifts={warehouseShifts}
        workCenters={warehouseWorkCenters}
      />

      {/* Work order detail */}
      <WorkOrderDetailDrawer workOrderId={detailId} onClose={() => setDetailId(null)} />

      {/* Modals */}
    </AppLayout>
  );
}
